<template>
    <teleport v-if="target" :to="target">
        <div class="season-backdrop" :class="{'is-inside': inside}" aria-hidden="true">
            <!-- Natal: neve e vagalumes piscando no fundo escurecido do modal -->
            <season-sky v-if="!newYear" :density="0.65 * intensity" :max-size="6.5" :speed="0.8" bokeh
                        :fireflies="16"></season-sky>
            <!-- Réveillon: fogos de artifício no lugar da neve e dos vagalumes -->
            <canvas v-else-if="!reduced" ref="fireworks" class="season-backdrop-canvas"></canvas>
        </div>
    </teleport>
</template>

<script>
// Festas no fundo dos modais. Acompanha o DOM e, quando há um modal aberto, põe o céu entre o fundo escurecido e a
// caixa do modal:
//   - modal do Bootstrap (components/modal.vue, cropper): o fundo é o .modal-backdrop (z-index 1040) e o modal fica
//     em 1050; o céu vai para o body, fixo em 1045, entre os dois (acima do desfoque, então fica nítido)
//   - modal próprio (.modal-mask, z-index 9998, com a caixa dentro): o céu entra na própria máscara com z-index -1,
//     que pinta logo acima do fundo da máscara e abaixo da caixa
// O cartão de festas (SeasonModal) tem o próprio céu e não usa nenhum dos dois, então não é afetado.
// Só monta na época e com a animação ligada (layouts/App.vue); com "reduzir movimento" não anima.
import {mapState} from 'vuex';
import SeasonSky from '@/components/season/SeasonSky.vue';
import {createModalFireworks} from '@/Helpers/global/seasonModalFireworks';
import {prefersReducedMotion, seasonIntensity} from '@/Helpers/global/season';

export default {
    name: 'SeasonBackdrop',
    components: {SeasonSky},
    data() {
        return {
            target: null,
            inside: false,
            reduced: prefersReducedMotion(),
        };
    },
    computed: {
        ...mapState({
            newYear: 'seasonNewYear',
            intensity: state => seasonIntensity(state.seasonNow),
        }),
    },
    watch: {
        // Abriu, fechou, trocou de modal ou virou o ano: refaz os fogos no canvas novo
        target() {
            this.$nextTick(this.syncFireworks);
        },
        newYear() {
            this.$nextTick(this.syncFireworks);
        },
    },
    mounted() {
        this.scan = this.scan.bind(this);
        this.queueScan = this.queueScan.bind(this);
        this.onResize = () => this.fireworks?.resize();
        // Bootstrap põe e tira o .modal-backdrop no body e troca a classe .show; os modais próprios entram com v-if.
        // A checagem roda no máximo uma vez por quadro
        this.observer = new MutationObserver(this.queueScan);
        this.observer.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['class']});
        window.addEventListener('resize', this.onResize);
        this.scan();
    },
    beforeUnmount() {
        this.observer?.disconnect();
        cancelAnimationFrame(this.scanFrame);
        window.removeEventListener('resize', this.onResize);
        this.stopFireworks();
    },
    methods: {
        queueScan(mutations) {
            // Ignora o que o próprio céu mexe
            if (mutations && mutations.every(m => m.target.closest?.('.season-backdrop'))) return;
            if (this.scanFrame) return;
            this.scanFrame = requestAnimationFrame(() => {
                this.scanFrame = 0;
                this.scan();
            });
        },
        scan() {
            const masks = document.querySelectorAll('.modal-mask');
            const mask = masks[masks.length - 1];
            if (mask) {
                this.place(mask, true);
            } else if (document.querySelector('.modal-backdrop')) {
                this.place(document.body, false);
            } else {
                this.place(null, false);
            }
        },
        place(target, inside) {
            if (this.target !== target) this.target = target;
            if (this.inside !== inside) this.inside = inside;
        },
        syncFireworks() {
            this.stopFireworks();
            const canvas = this.$refs.fireworks;
            if (!canvas || !this.newYear || this.reduced) return;
            // Mesmos fogos do fundo dos modais do R2 Security: sobem pelas laterais, o meio fica atrás da caixa
            this.fireworks = createModalFireworks(canvas);
        },
        stopFireworks() {
            this.fireworks?.destroy();
            this.fireworks = null;
        },
    },
};
</script>

<style>
.season-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1045;
    pointer-events: none;
    overflow: hidden;
    animation: season-backdrop-in 0.6s ease both;
}

/* Dentro da .modal-mask: logo acima do fundo da máscara e abaixo da caixa do modal */
.season-backdrop.is-inside {
    position: absolute;
    z-index: -1;
}

.season-backdrop-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}

@keyframes season-backdrop-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .season-backdrop {
        display: none;
    }
}
</style>
